export type MachineStatus = "Actif" | "Hors ligne" | "Maintenance" | "Erreur";
export type MachineClass = "Classe A" | "Classe B" | "Classe C";

export type Template = {
  id: string;
  name: string;
  vendor: string;
  model: string;
  driver: string;
  host: string;
  port: number;
  classification: MachineClass;
  scope: string;
  description: string;
  tags: string[];
  installs: number;
  autoTest: "daily" | "hourly" | "off";
  policies: { tls: boolean; mtls: boolean; ipWhitelist: boolean; pseudo: boolean; audit: boolean };
  calibration: { intervalDays: number; next: string };
  scenarios: string[];
};

export type Instance = {
  id: string;
  name: string;
  templateId: string | null;
  status: MachineStatus;
  host: string;
  port: number;
  firmware: string;
  protocol: "ASTM" | "HL7" | "IEEE 11073" | "PROPRIETARY" | "DICOM";
  nextCalibration: string | null;
};

export const TEMPLATES: Template[] = [
  {
    id: "tpl-roche-cobas",
    name: "Roche Cobas — Laboratoire central",
    vendor: "Roche",
    model: "Cobas e411",
    driver: "@dikdoc/driver-roche-cobas@2.3.1",
    host: "10.42.1.10",
    port: 2575,
    classification: "Classe B",
    scope: "LAB",
    description: "Analyseur immuno-sérologique pour dosage hormonal, marqueurs tumoraux, vitamines",
    tags: ["LAB", "HEMA", "mTLS"],
    installs: 4,
    autoTest: "daily",
    policies: { tls: true, mtls: true, ipWhitelist: true, pseudo: true, audit: true },
    calibration: { intervalDays: 30, next: "2026-06-14" },
    scenarios: ["ping", "astm-handshake", "hl7-ack", "throughput"],
  },
  {
    id: "tpl-philips-intellivue",
    name: "Philips IntelliVue — Réanimation",
    vendor: "Philips",
    model: "IntelliVue MX800",
    driver: "@dikdoc/driver-philips-intellivue@1.1.4",
    host: "10.42.5.20",
    port: 11112,
    classification: "Classe C",
    scope: "ICU",
    description: "Moniteur patient multi-paramétrique (ECG, SpO2, NIBP, T°, FR) avec alarmes",
    tags: ["ICU", "URG", "CHIR", "mTLS"],
    installs: 8,
    autoTest: "hourly",
    policies: { tls: true, mtls: true, ipWhitelist: true, pseudo: true, audit: true },
    calibration: { intervalDays: 14, next: "2026-07-21" },
    scenarios: ["ping", "hl7-ack", "alarm-relay"],
  },
  {
    id: "tpl-fujifilm-fcr",
    name: "Fujifilm FCR — Imagerie",
    vendor: "Fujifilm",
    model: "FCR Capsula X",
    driver: "@dikdoc/driver-fujifilm-fcr@2.0.5",
    host: "10.42.7.30",
    port: 104,
    classification: "Classe B",
    scope: "IMAG",
    description: "Computed Radiography pour radio standard (thorax, abdomen, os)",
    tags: ["IMAG", "URG", "ORTH", "mTLS"],
    installs: 2,
    autoTest: "daily",
    policies: { tls: true, mtls: true, ipWhitelist: false, pseudo: true, audit: true },
    calibration: { intervalDays: 90, next: "2026-09-06" },
    scenarios: ["ping", "dicom-cstore", "mwl-query"],
  },
  {
    id: "tpl-medtronic-cgm",
    name: "Medtronic Guardian — CGM",
    vendor: "Medtronic",
    model: "Guardian Connect",
    driver: "@dikdoc/driver-medtronic-guardian@1.2.3",
    host: "api.medtronic-diabetes.com",
    port: 443,
    classification: "Classe B",
    scope: "ENDO",
    description: "Continuous Glucose Monitoring pour patients diabétiques ambulatoires",
    tags: ["ENDO", "DIAB", "MED"],
    installs: 12,
    autoTest: "hourly",
    policies: { tls: true, mtls: false, ipWhitelist: false, pseudo: true, audit: true },
    calibration: { intervalDays: 60, next: "2027-02-01" },
    scenarios: ["ping", "https-pull", "glucose-stream"],
  },
];

export const INSTANCES: Instance[] = [
  { id: "inst-1", name: "Roche Cobas e411", templateId: "tpl-roche-cobas", status: "Actif", host: "10.42.1.10", port: 2575, firmware: "1.0.0", protocol: "ASTM", nextCalibration: "2026-06-14" },
  { id: "inst-2", name: "Siemens Atellica IM", templateId: null, status: "Actif", host: "10.42.2.11", port: 2576, firmware: "2.1.3", protocol: "HL7", nextCalibration: null },
  { id: "inst-3", name: "Mindray BC-6800", templateId: null, status: "Actif", host: "10.42.3.12", port: 104, firmware: "3.2.6", protocol: "ASTM", nextCalibration: null },
  { id: "inst-4", name: "Philips IntelliVue MX800", templateId: "tpl-philips-intellivue", status: "Hors ligne", host: "10.42.4.13", port: 11112, firmware: "1.3.9", protocol: "IEEE 11073", nextCalibration: "2026-09-06" },
  { id: "inst-5", name: "GE CARESCAPE B650", templateId: null, status: "Maintenance", host: "10.42.5.14", port: 4242, firmware: "2.4.2", protocol: "IEEE 11073", nextCalibration: null },
  { id: "inst-6", name: "Dräger Infinity M540", templateId: null, status: "Erreur", host: "10.42.1.15", port: 8443, firmware: "3.5.5", protocol: "HL7", nextCalibration: null },
  { id: "inst-7", name: "Medtronic Guardian Connect", templateId: "tpl-medtronic-cgm", status: "Actif", host: "10.42.2.16", port: 2575, firmware: "1.6.8", protocol: "PROPRIETARY", nextCalibration: "2027-02-01" },
  { id: "inst-8", name: "Dexcom G6 CGM", templateId: null, status: "Actif", host: "10.42.3.17", port: 2576, firmware: "2.7.1", protocol: "PROPRIETARY", nextCalibration: null },
  { id: "inst-9", name: "Abbott FreeStyle Libre 3", templateId: null, status: "Actif", host: "10.42.4.18", port: 104, firmware: "3.8.4", protocol: "PROPRIETARY", nextCalibration: null },
  { id: "inst-10", name: "Beckman AU680", templateId: null, status: "Actif", host: "10.42.5.19", port: 2575, firmware: "1.2.0", protocol: "ASTM", nextCalibration: null },
  { id: "inst-11", name: "Carestream DRX", templateId: "tpl-fujifilm-fcr", status: "Actif", host: "10.42.6.20", port: 104, firmware: "4.0.1", protocol: "DICOM", nextCalibration: "2026-09-06" },
  { id: "inst-12", name: "GE Logiq E10", templateId: null, status: "Actif", host: "10.42.7.21", port: 104, firmware: "2.5.0", protocol: "DICOM", nextCalibration: null },
];
